import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const STATUS_STEPS = [

    {
        id: "confirmed",
        label: "Order Confirmed",
        completedLabel: "Confirmed",
        icon: "/image/check-circle.svg",
    },
    {
        id: "processing",
        label: "Order Processed",
        completedLabel: "Processed",
        icon: "/image/package.svg",
    },
    {
        id: "shipped",
        label: "Shipped",
        completedLabel: "Shipped",
        icon: "/image/truck.svg",
    },
    {
        id: "out_for_delivery",
        label: "Out for Delivery",
        completedLabel: "Out for Delivery",
        icon: "/image/delivery.svg",
    },
    {
        id: "delivered",
        label: "Delivered",
        completedLabel: "Delivered",
        icon: "/image/check-circle.svg",
    },
];

export const fetchOrderTracking = createAsyncThunk(
    'trackOrder/fetchOrderTracking',
    async (orderId, { rejectWithValue }) => {
        try {
            const response = await callAPI(
                `${apiUrls.orderStatus}/${orderId}`,
                {},
                "GET",
                {}
            );

            if (response?.data?.success && response?.data?.data) {
                const order = response.data.data;
                
                return {
                    id: order.id,
                    orderId: order.id,
                    order_number: order.order_number,
                    created_at: order.created_at,
                    updated_at: order.updated_at,
                    total_amount: order.total_amount,
                    subtotal: order.subtotal,
                    tax: order.tax,
                    shipping_charge: order.shipping_charge,
                    status: order.status,
                    payment_status: order.payment_status,
                    payment_method: order.payment_method,
                    transaction_id: order.transaction_id,
                    user: order.user,
                    billing_address: order.billing_address,
                    shipping_address: order.shipping_address,
                    statuses: order.statuses || [],
                    items: order.items || [],
                    payments: order.payments || []
                };
            }
            return rejectWithValue(response?.data?.message || 'Order not found');
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch order tracking');
        }
    }
);

const trackOrderSlice = createSlice({
    name: 'trackOrder',
    initialState: {
        orderData: null,
        orderId: null,
        statusSteps: STATUS_STEPS,
        statusDates: {},
        loading: false,
        orderLoading: false,
        error: null,
        message: { type: "", text: "" },
        backLinkText: "Back",
        backLinkUrl: "/my-order"
    },
    reducers: {
        setOrderId: (state, action) => {
            state.orderId = action.payload;
        },
        clearOrderData: (state) => {
            state.orderData = null;
        },
        clearTrackOrderError: (state) => {
            state.error = null;
        },
        setMessage: (state, action) => {
            state.message = {
                type: action.payload.type,
                text: action.payload.text
            };
        },
        clearMessage: (state) => {
            state.message = { type: "", text: "" };
        },
        resetTrackOrderState: (state) => {
            state.orderData = null;
            state.orderId = null;
            state.statusDates = {};
            state.loading = false;
            state.error = null;
            state.message = { type: "", text: "" };
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrderTracking.pending, (state) => {
                state.loading = true;
                state.orderLoading = true;
                state.error = null;
            })
            .addCase(fetchOrderTracking.fulfilled, (state, action) => {
                state.loading = false;
                state.orderLoading = false;
                state.orderData = action.payload;
                state.orderId = action.payload.orderId;
            })
            .addCase(fetchOrderTracking.rejected, (state, action) => {
                state.loading = false;
                state.orderLoading = false;
                state.error = action.payload || 'Failed to fetch order tracking';
                state.message = {
                    type: 'error',
                    text: action.payload || 'Failed to fetch order tracking'
                };
            });
    }
});

export const {
    setOrderId,
    clearOrderData,
    clearTrackOrderError,
    setMessage,
    clearMessage,
    resetTrackOrderState
} = trackOrderSlice.actions;

export default trackOrderSlice.reducer;